/*
 * CountdownBadge.jsx
 * ------------------
 * Displays a countdown to a checklist's target date.
 * Shows days remaining in a colored pill.
 * Green for plenty of time, gold for soon, red for imminent or past.
 * Used on ChecklistCard and ChecklistDetail header.
 */

import React from "react";

// ============================================
// HELPERS
// ============================================
function getDaysRemaining(targetDate) {
  const now = new Date();
  const target = new Date(targetDate);
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  const diffMs = target - todayStart;
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  return diffDays;
}

function getConfig(days) {
  if (days < 0) return {
    bg: "var(--status-overdue-muted)",
    color: "var(--status-overdue)",
    icon: "⚠️",
    label: `${Math.abs(days)} day${Math.abs(days) !== 1 ? "s" : ""} overdue`
  };

  if (days === 0) return {
    bg: "var(--status-overdue-muted)",
    color: "var(--status-overdue)",
    icon: "🚨",
    label: "Due today"
  };

  if (days <= 3) return {
    bg: "var(--accent-gold-muted)",
    color: "var(--accent-gold)",
    icon: "⏳",
    label: `${days} day${days !== 1 ? "s" : ""} left`
  };

  if (days <= 7) return {
    bg: "var(--accent-gold-muted)",
    color: "var(--accent-gold)",
    icon: "📅",
    label: `${days} days left`
  };

  return {
    bg: "var(--accent-green-muted)",
    color: "var(--accent-green)",
    icon: "📅",
    label: `${days} days left`
  };
}

// ============================================
// MAIN EXPORT
// ============================================
export default function CountdownBadge({ targetDate, size = "sm" }) {
  if (!targetDate) return null;

  const days = getDaysRemaining(targetDate);
  const config = getConfig(days);

  const sizes = {
    sm: { fontSize: "11px", padding: "2px 8px", gap: "3px" },
    md: { fontSize: "13px", padding: "4px 12px", gap: "4px" },
    lg: { fontSize: "15px", padding: "6px 16px", gap: "6px" }
  };

  const sizeStyle = sizes[size] || sizes.sm;

  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: sizeStyle.gap,
      background: config.bg,
      color: config.color,
      fontSize: sizeStyle.fontSize,
      fontFamily: "var(--font-body)",
      fontWeight: 600,
      padding: sizeStyle.padding,
      borderRadius: "100px",
      border: `1px solid ${config.color}`,
      whiteSpace: "nowrap"
    }}>
      <span style={{ fontSize: sizeStyle.fontSize }}>
        {config.icon}
      </span>
      {config.label}
    </span>
  );
}